import React from 'react';
import { Box, Typography, Button, Avatar, Chip, useTheme } from "@mui/material";
import QuizIcon from '@mui/icons-material/Quiz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { tokens } from "../../../theme";
import dayjs from 'dayjs';

var utc = require('dayjs/plugin/utc');
var timezone = require('dayjs/plugin/timezone');
dayjs.extend(utc);
dayjs.extend(timezone);

const fmt = (d) => (d ? dayjs(d).tz('America/New_York').format('MMM D, YYYY h:mm A') : null);

// Box for a prep PRACTICE exam (Phase 7). Two shapes:
//  • personal (student): taken chip / due + "Open in ETT".
//  • roster (teacher/admin, item.students present): per-student taken chips + a count.
const EttExamBox = ({ item }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const roster = Array.isArray(item.students);
    const overdue = item.due_date && !item.taken && new Date() > new Date(item.due_date);
    const takenCount = roster ? item.students.filter((s) => s.taken).length : 0;
    const lateCount = roster ? item.students.filter((s) => s.taken && s.taken_late).length : 0;
    // Active = release date has passed (takeable now); Upcoming = not released yet.
    const active = !item.release_date || new Date(item.release_date) <= new Date();

    // Short guidance shown in the box body. Student view is state-aware; the roster
    // (teacher/admin) view gets a neutral one-liner.
    const studentNote = item.taken
        ? (item.taken_late ? "Practice test finished after the due date." : "Practice test completed — nice work!")
        : item.due_date
            ? "Finish the practice test in ETT before the due date."
            : "Open and finish the practice test in ETT.";
    const rosterNote = item.due_date
        ? "Finish the practice test in ETT before the due date."
        : "Finish the practice test in ETT.";

    return (
        <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: colors.primary[600], borderRadius: "5px", border: "0.1px solid #a3a3a3", mb: "1.8rem", opacity: active ? 1 : 0.6 }}>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%", borderBottom: "0.1px solid #a3a3a3", py: "0.5rem", px: "1rem", alignItems: "center" }}>
                <Avatar sx={{ width: 30, height: 30 }}><QuizIcon /></Avatar>
                <Typography variant="h5" sx={{ my: "auto", ml: "1rem" }}>{item.exam_title}</Typography>
                <Chip size="small" label="Practice Test" sx={{ ml: 1 }} />
                <Chip size="small" variant="outlined" color={active ? "success" : "default"} label={active ? "Active" : "Upcoming"} sx={{ ml: 0.5 }} />
                {roster ? (
                    <Typography variant="h7" color={colors.grey[700]} sx={{ my: "auto", ml: "auto" }}>
                        {takenCount}/{item.students.length} taken{lateCount ? ` (${lateCount} late)` : ""}{item.due_date ? ` · due ${fmt(item.due_date)}` : ""}
                    </Typography>
                ) : item.taken ? (
                    <Chip size="small" color={item.taken_late ? "warning" : "success"} label={item.taken_late ? "Taken late" : "Taken"} sx={{ ml: "auto" }} />
                ) : item.due_date ? (
                    <Typography variant="h7" color={overdue ? "error" : colors.grey[700]} sx={{ my: "auto", ml: "auto" }}>
                        <span style={{ fontWeight: "bold" }}>Due</span> {fmt(item.due_date)}
                    </Typography>
                ) : null}
            </Box>
            {roster ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem", py: "0.75rem", px: "1rem" }}>
                    <Typography variant="body2" color={colors.grey[700]}>{rosterNote}</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {item.students.length === 0 ? (
                            <Typography variant="caption" color={colors.grey[700]}>No students assigned.</Typography>
                        ) : (
                            item.students.map((s) => (
                                <Chip key={s.student_id} size="small" color={s.taken ? (s.taken_late ? "warning" : "success") : "default"} label={s.name} />
                            ))
                        )}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem", py: "1rem", px: "1rem" }}>
                    <Typography variant="body2" color={overdue ? "error" : colors.grey[700]}>{studentNote}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h7" color={colors.grey[700]}>
                            {active
                                ? (item.release_date ? `Available ${fmt(item.release_date)}` : "Practice exam in ETT")
                                : `Opens ${fmt(item.release_date)}`}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            disabled={!item.ett_deep_link || !active}
                            onClick={() => item.ett_deep_link && window.open(item.ett_deep_link, "_blank", "noopener")}
                        >
                            {active ? "Open in ETT" : "Not open yet"}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default EttExamBox;
